import { useState, useMemo, useEffect, useRef, useLayoutEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import {
  IconChevronDown,
  IconChevronRight,
  IconPaperclip,
  IconLoader2,
  IconExternalLink,
  IconAlertTriangle,
  IconInfoCircle,
  IconFileText,
  IconWallet,
  IconCalendarEvent,
  IconUsers,
} from "@tabler/icons-react";
import { Guest } from "@/types/Guest";
import { Attachment } from "@/types/Attachment";
import { EmailTemplate, DirectUploadFile } from "@/types/EmailTemplate";
import { ConditionGroup } from "@/types/EventRule";
import { DocumentTemplate } from "@/types/DocumentTemplate";
import { DocumentType } from "@/types/enums/DocumentType";
import { useCapabilitiesOptional } from "@/providers/CapabilitiesProvider";
import { useEventTimezone } from "@/providers/EventProvider";
import { useFieldsOptional } from "@/providers/FieldsProvider";
import { buildPreviewFormatContext } from "@/lib/utils/additional-field-format";
import { WalletPreview } from "@/components/builders/wallet/WalletPreview";
import {
  applyPassRenderRules,
  namedUncertainRows,
  resolveWalletPreviewLang,
  classifyUncertainRows,
  pickByCertainty,
} from "@/components/builders/wallet/passRenderRules";
import type { DroppedField } from "@/components/builders/wallet/passRenderRules";
import {
  buildSeatVariables,
  withSeatVariables,
  guestHasSeat,
  activityRollupEmpty,
} from "@/lib/utils/seat-variables";
import { useEventVariables } from "@/hooks/useEventVariables";
import { CalendarPreview } from "@/components/builders/calendar/CalendarPreview";
import {
  getGuestAttachments,
  getDownloadUrl,
} from "@/lib/api/handlers/AttachmentHandling";
import { getEmailTemplate } from "@/lib/api/handlers/EmailTemplateHandling";
import {
  getDocumentTemplateHandler,
  getDocumentTemplatesHandler,
  downloadDocumentTemplatePdfForGuest,
} from "@/lib/api/handlers/DocumentTemplateHandling";
import { getCompanions } from "@/lib/api/handlers/GuestHandling";
import { evaluateConditionGroup } from "@/lib/utils/evaluate-condition";
import {
  knowsToken,
  replaceVariablesInWalletConfig,
  replaceVariablesInCalendarConfig,
} from "@/lib/utils/variable-replacer";
import { resolveLangValue } from "@/lib/utils/resolve-lang";
import { formatFileSize } from "@/lib/utils/email-attachment-validation";
import {
  FileKind,
  FILE_KIND_STYLE,
  getFileKind,
  buildAssetUrl,
} from "@/lib/utils/attachment-display";

// Server hard-blocks application/zip from email delivery (still a portal link),
// so we exclude it from the preview too. Mirrors InternalEmailResource.
const ZIP_CONTENT_TYPE = "application/zip";
// Aggregate budget the backend enforces per email (18 MB). We can't replicate
// the exact drop order, so we only surface a soft warning past it.
const AGGREGATE_BUDGET = 18 * 1024 * 1024;
// A4 @ 96dpi — backend's fallback when a document template has no dimensions.
const DEFAULT_DOC_WIDTH = 794;
const DEFAULT_DOC_HEIGHT = 1123;

/** The template-level attachment config the preview resolves against. */
type AttachmentConfig = {
  attachments: string[];
  directUploadFiles: DirectUploadFile[];
  directUploadFileConditions: Record<string, ConditionGroup>;
};

type Props = {
  eventId: string;
  guest: Guest | null;
  /** Send-modal path: fetch the SAVED template by id. */
  emailTemplateId?: string;
  /** Builder path: pass the LIVE (possibly unsaved) attachment config directly,
   *  bypassing the fetch so the preview reflects in-editor edits immediately. */
  liveAttachments?: AttachmentConfig;
  /** Floating overlay styling (builder preview). Adds a card surface + shadow to
   *  the outer container so it reads over the canvas. Off → flush (send modal). */
  floating?: boolean;
};

// Rows carry the guest their preview should render for — the main recipient,
// or (for companion copies) the companion — and, when it's a companion copy,
// that companion's display name for the row badge.
type RowGuestFields = {
  renderGuest: Guest | null;
  companionName?: string;
};

type FileRow = RowGuestFields & {
  type: "file";
  id: string;
  name: string;
  size: number;
  fileKind: FileKind;
  contentType: string;
  // Underlying storage key — used to dedup the same physical file reachable via
  // multiple attachments (mirrors the server's dedup-by-s3Key).
  s3Key?: string;
  // Category attachment file → fetch a presigned URL on expand.
  attachmentId?: string;
  attachmentFileId?: string;
  // Direct-upload file → preview via the public asset host (no presign endpoint).
  assetUrl?: string | null;
};

type DocRow = RowGuestFields & {
  type: "doc";
  id: string;
  name: string;
  documentTemplateId: string;
};

type Row = FileRow | DocRow;

function isZip(contentType?: string): boolean {
  return (contentType ?? "").toLowerCase() === ZIP_CONTENT_TYPE;
}

// Icon + colour per document-builder TYPE, so a linked doc row reads as what it
// actually is (a wallet pass / calendar file / document), not a generic "doc".
const DOC_TYPE_ICON: Record<
  DocumentType,
  { Icon: typeof IconFileText; color: string }
> = {
  [DocumentType.STANDARD]: { Icon: IconFileText, color: "text-rose-500" },
  [DocumentType.WALLET]: { Icon: IconWallet, color: "text-amber-500" },
  [DocumentType.CALENDAR]: { Icon: IconCalendarEvent, color: "text-sky-500" },
};

// pSendEmail i18n key for each type's source badge.
const DOC_TYPE_LABEL_KEY: Record<DocumentType, string> = {
  [DocumentType.STANDARD]: "attachmentsSourceDocument",
  [DocumentType.WALLET]: "attachmentsSourceWallet",
  [DocumentType.CALENDAR]: "attachmentsSourceCalendar",
};

export const AttachmentPreview: React.FC<Props> = ({
  eventId,
  emailTemplateId,
  guest,
  liveAttachments,
  floating = false,
}) => {
  const t = useTranslations("pSendEmail");
  // The email builder mounts this OUTSIDE EventLayoutShell, so there's no
  // CapabilitiesProvider there — use the non-throwing optional hook. The builder
  // path always passes `liveAttachments`; treat that as allowed (the route is
  // auth-gated and every underlying read is server-cap-enforced → a capless user
  // just gets 403s that degrade to an empty panel). The send modal (no
  // liveAttachments) keeps the real `attachments:read` gate, identical to before.
  const { can } = useCapabilitiesOptional();
  const canReadAttachments = liveAttachments ? true : can("attachments:read");

  const [open, setOpen] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const guestId = guest?.guestId;

  // Template-level attachment config (categories + direct uploads + per-file
  // conditions). `directUploadFileConditions` requires the backend allowlist fix.
  // Skipped entirely when the caller supplies `liveAttachments` (builder path).
  const templateQ = useQuery({
    queryKey: ["sendEmailTemplateAttachments", eventId, emailTemplateId],
    queryFn: () =>
      getEmailTemplate(
        eventId,
        emailTemplateId!,
        "attachments,directUploadFiles,directUploadFileConditions",
      ),
    enabled: canReadAttachments && !liveAttachments && !!eventId && !!emailTemplateId,
  });

  // Per-guest, condition-resolved + file-scoped attachments (same endpoint the
  // send path intersects against). 403s for roles lacking attachments:read.
  const guestAttQ = useQuery({
    queryKey: ["guestAttachments", eventId, guestId],
    queryFn: () => getGuestAttachments(eventId, guestId!),
    enabled: canReadAttachments && !!eventId && !!guestId,
  });

  const fetchedTemplate: EmailTemplate | null =
    templateQ.data && !("isServerError" in templateQ.data)
      ? templateQ.data
      : null;

  // Live (builder) config wins; otherwise fall back to the fetched template.
  const cfg: AttachmentConfig | null = liveAttachments
    ? liveAttachments
    : fetchedTemplate
      ? {
          attachments: fetchedTemplate.attachments ?? [],
          directUploadFiles: fetchedTemplate.directUploadFiles ?? [],
          directUploadFileConditions:
            fetchedTemplate.directUploadFileConditions ?? {},
        }
      : null;

  const guestAttachments: Attachment[] | null = Array.isArray(guestAttQ.data)
    ? guestAttQ.data
    : null;

  // Document-template TYPE map (id → STANDARD/WALLET/CALENDAR) so a linked
  // doc-builder row is badged/iconed by its real kind (a wallet pass no longer
  // reads "Dokument"). Lightweight list; degrades to STANDARD on failure/403.
  const docTemplatesQ = useQuery({
    queryKey: ["attachmentPreviewDocTypes", eventId],
    queryFn: () => getDocumentTemplatesHandler(eventId),
    enabled: canReadAttachments && !!eventId,
  });
  const docTypeById = useMemo(() => {
    const m = new Map<string, DocumentType>();
    const list = Array.isArray(docTemplatesQ.data) ? docTemplatesQ.data : [];
    for (const d of list) {
      if (d.documentTemplateId) m.set(d.documentTemplateId, d.documentType);
    }
    return m;
  }, [docTemplatesQ.data]);

  // Opt-in attachment ids for companion COPIES: linked + ATTACH_TO_EMAIL +
  // includeCompanionCopies, mirroring InternalEmailResource's optInIds. Derived
  // from the main recipient's resolved view (the common ALL/GENERAL-targeted
  // case); FILTER-targeted attachments the MAIN doesn't match but a companion
  // does aren't previewed (edge case, matches send only approximately).
  const linkedIds = cfg?.attachments;
  const optInCompanionIds = useMemo(() => {
    const s = new Set<string>();
    if (!guestAttachments || !linkedIds) return s;
    const linked = new Set(linkedIds);
    for (const a of guestAttachments) {
      if (
        linked.has(a.attachmentId) &&
        a.accessType === "ATTACH_TO_EMAIL" &&
        a.includeCompanionCopies === true
      ) {
        s.add(a.attachmentId);
      }
    }
    return s;
  }, [guestAttachments, linkedIds]);

  // Companions of the main recipient, deliberately WITHOUT a `scope`: the send
  // path this preview has to match is `EmailAttachmentCollector.addCompanionCopies`,
  // which calls the un-scoped `GuestRepository.getCompanions` (DECLINED
  // excluded, archived and PENDING included). Naming a narrower scope here
  // would make the preview show fewer copies than the send actually attaches —
  // the two must ask the same question, whatever that question is.
  // Only fetched when the recipient actually has an opt-in companion-copy
  // attachment.
  const companionsQ = useQuery({
    queryKey: ["attachmentPreviewCompanions", eventId, guestId],
    queryFn: () =>
      getCompanions(
        eventId,
        guestId!,
        // Broad field set so wallet/calendar var-substitution (which reads
        // arbitrary guest keys + additionalFields) resolves per companion.
        //
        // `seatInfo` and `seatCount` are not for substitution — nothing reads
        // them as tokens. They are what `guestHasSeat` needs to tell "this
        // companion holds no seat" apart from "nobody asked". Without them a
        // seated companion's seat rows were reported to the operator as missing
        // from their pass, on the screen right before Send.
        "guestId,firstName,lastName,email,phone,gender,language,salutation,jobTitle,company,additionalFields,seatInfo,seatCount",
      ),
    enabled:
      canReadAttachments && !!eventId && !!guestId && optInCompanionIds.size > 0,
  });
  const companions: Guest[] = useMemo(
    () => (Array.isArray(companionsQ.data) ? companionsQ.data : []),
    [companionsQ.data],
  );
  const companionIdKey = companions.map((c) => c.guestId).join(",");

  // Each companion's OWN resolved attachment view (their targetType/condition
  // enforced server-side) → keyed by companion id. One combined query.
  const companionAttsQ = useQuery({
    queryKey: ["attachmentPreviewCompanionAtts", eventId, guestId, companionIdKey],
    queryFn: async () => {
      const map: Record<string, Attachment[]> = {};
      await Promise.all(
        companions.map(async (c) => {
          if (!c.guestId) return;
          const res = await getGuestAttachments(eventId, c.guestId);
          if (Array.isArray(res)) map[c.guestId] = res;
        }),
      );
      return map;
    },
    enabled:
      canReadAttachments &&
      !!eventId &&
      companions.length > 0 &&
      optInCompanionIds.size > 0,
  });
  const companionAttsMap = useMemo(
    () => (companionAttsQ.data && !("isServerError" in companionAttsQ.data)
      ? companionAttsQ.data
      : {}),
    [companionAttsQ.data],
  );

  const guestRecord = guest as unknown as Record<string, unknown> | null;

  const cfgAttachments = cfg?.attachments;
  const cfgDirectFiles = cfg?.directUploadFiles;
  const cfgDirectConditions = cfg?.directUploadFileConditions;

  const rows = useMemo<Row[]>(() => {
    if (!cfg) return [];
    const out: Row[] = [];
    // Dedup the same physical file / document reachable via several attachments,
    // mirroring the server (partition dedups by s3Key; doc ids via a set).
    const seenFiles = new Set<string>();
    const seenDocs = new Set<string>();

    // 1. Category attachments selected on the template, resolved for this guest.
    const linked = new Set(cfg.attachments ?? []);
    if (guestAttachments) {
      for (const a of guestAttachments) {
        if (a.accessType !== "ATTACH_TO_EMAIL") continue;
        if (!linked.has(a.attachmentId)) continue;
        const src = a.sourceType;
        if ((src === "UPLOAD" || src === "MIXED") && a.files) {
          for (const f of a.files) {
            if (isZip(f.contentType)) continue;
            const dedupKey = f.s3Key || f.attachmentFileId;
            if (seenFiles.has(dedupKey)) continue;
            seenFiles.add(dedupKey);
            out.push({
              type: "file",
              id: `f-${a.attachmentId}-${f.attachmentFileId}`,
              name: f.fileName,
              size: f.fileSize,
              fileKind: getFileKind(f.fileName, f.contentType),
              contentType: f.contentType,
              s3Key: f.s3Key,
              attachmentId: a.attachmentId,
              attachmentFileId: f.attachmentFileId,
              renderGuest: guest,
            });
          }
        }
        if (
          (src === "DOCUMENT_BUILDER" || src === "MIXED") &&
          a.documentBuilderTemplateIds
        ) {
          for (const docId of a.documentBuilderTemplateIds) {
            if (seenDocs.has(docId)) continue;
            seenDocs.add(docId);
            out.push({
              type: "doc",
              id: `d-${a.attachmentId}-${docId}`,
              name: a.name,
              documentTemplateId: docId,
              renderGuest: guest,
            });
          }
        }
      }
    }

    // 2. Direct-upload files, gated by per-file conditions (empty ⇒ attached).
    for (const f of cfg.directUploadFiles ?? []) {
      if (isZip(f.contentType)) continue;
      const cond = cfg.directUploadFileConditions?.[f.fileId];
      // No guest selected → can't evaluate; show unconditionally as best-effort.
      const passes = guestRecord
        ? evaluateConditionGroup(cond, guestRecord)
        : true;
      if (!passes) continue;
      const dedupKey = f.s3Key || f.fileId;
      if (seenFiles.has(dedupKey)) continue;
      seenFiles.add(dedupKey);
      out.push({
        type: "file",
        id: `u-${f.fileId}`,
        name: f.fileName,
        size: f.fileSize,
        fileKind: getFileKind(f.fileName, f.contentType),
        contentType: f.contentType,
        s3Key: f.s3Key,
        assetUrl: buildAssetUrl(f.s3Key),
        renderGuest: guest,
      });
    }

    // 3. Companion COPIES — for each accepted companion, the opted-in
    //    attachments contribute their INDIVIDUAL uploaded files + doc-builder
    //    templates, rendered for THAT companion. Mirrors
    //    InternalEmailResource.collectCompanionCopies (GENERAL uploads are the
    //    identical shared file → not duplicated per companion; docs render
    //    personalized). Each companion's rows carry their name for the badge.
    if (optInCompanionIds.size > 0) {
      for (const c of companions) {
        const cid = c.guestId;
        if (!cid) continue;
        const catts = companionAttsMap[cid];
        if (!catts) continue;
        const cName = `${c.firstName ?? ""} ${c.lastName ?? ""}`.trim();
        const seenCDocs = new Set<string>();
        for (const a of catts) {
          if (!optInCompanionIds.has(a.attachmentId)) continue;
          const src = a.sourceType;
          if (
            a.assignmentType === "INDIVIDUAL" &&
            (src === "UPLOAD" || src === "MIXED") &&
            a.files
          ) {
            for (const f of a.files) {
              if (isZip(f.contentType)) continue;
              out.push({
                type: "file",
                id: `cf-${cid}-${a.attachmentId}-${f.attachmentFileId}`,
                name: f.fileName,
                size: f.fileSize,
                fileKind: getFileKind(f.fileName, f.contentType),
                contentType: f.contentType,
                s3Key: f.s3Key,
                attachmentId: a.attachmentId,
                attachmentFileId: f.attachmentFileId,
                renderGuest: c,
                companionName: cName,
              });
            }
          }
          if (
            (src === "DOCUMENT_BUILDER" || src === "MIXED") &&
            a.documentBuilderTemplateIds
          ) {
            for (const docId of a.documentBuilderTemplateIds) {
              if (seenCDocs.has(docId)) continue;
              seenCDocs.add(docId);
              out.push({
                type: "doc",
                id: `cd-${cid}-${a.attachmentId}-${docId}`,
                name: a.name,
                documentTemplateId: docId,
                renderGuest: c,
                companionName: cName,
              });
            }
          }
        }
      }
    }

    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfgAttachments, cfgDirectFiles, cfgDirectConditions, guestAttachments, guestRecord, optInCompanionIds, companions, companionAttsMap]);

  const totalSize = rows.reduce(
    (sum, r) => (r.type === "file" ? sum + r.size : sum),
    0,
  );
  const overBudget = totalSize > AGGREGATE_BUDGET;

  // Hidden when the role can't read attachments, while loading, or when nothing
  // resolves — keeps the modal clean for templates with no attachments.
  if (!canReadAttachments) return null;
  const templateLoading = !liveAttachments && templateQ.isLoading;
  if (templateLoading || (!!guestId && guestAttQ.isLoading)) return null;
  if (rows.length === 0) return null;

  return (
    <div
      className={`flex flex-col border outline-quaternary rounded-xl p-2 ${
        floating ? "surface-card shadow-xl" : ""
      }`}
    >
      <button
        type="button"
        aria-expanded={open}
        className="px-2 py-1 flex flex-row items-center gap-1.5 hover:font-primary cursor-pointer w-full"
        onClick={() => setOpen((p) => !p)}
      >
        <IconPaperclip size={14} className="font-tertiary shrink-0" />
        <p className="body-2-medium font-secondary">
          {t("attachmentsTitle", { count: rows.length })}
        </p>
        {open ? (
          <IconChevronDown size={14} className="font-tertiary ml-auto" />
        ) : (
          <IconChevronRight size={14} className="font-tertiary ml-auto" />
        )}
      </button>

      {open && (
        // SINGLE scroll container for the whole expandable list. Capped in PIXELS
        // (not vh): the send modal is only `h-[min(720px,90vh)]`, so a vh cap
        // (e.g. 45vh ≈ 930px) is TALLER than the modal itself and a tall preview
        // spilled straight past the card. `min-h-0` is also required so this flex
        // child's `overflow-y-auto` actually clips instead of growing to content
        // height. Each preview is scaled to fit `PREVIEW_MAX_H`, so an expanded
        // row + its header fit here with room; the list scrolls between rows.
        <div className="flex flex-col gap-1.5 mt-2 max-h-80 min-h-0 overflow-y-auto -mx-1 px-1">
          {!guestId && (
            <p className="shrink-0 body-4-regular font-tertiary italic px-2">
              {t("attachmentPickRecipientHint")}
            </p>
          )}
          {rows.map((row) => {
            const isExpanded = expandedId === row.id;
            // Doc rows are badged/iconed by their real document TYPE (a wallet
            // pass reads "Wallet", a calendar file "Kalender", not "Dokument").
            const docType =
              row.type === "doc"
                ? docTypeById.get(row.documentTemplateId)
                : undefined;
            const { Icon, color } =
              row.type === "doc"
                ? DOC_TYPE_ICON[docType ?? DocumentType.STANDARD]
                : FILE_KIND_STYLE[row.fileKind];
            const sourceLabel =
              row.type === "doc"
                ? t(DOC_TYPE_LABEL_KEY[docType ?? DocumentType.STANDARD])
                : t("attachmentsSourceUpload");
            return (
              <div
                key={row.id}
                // shrink-0: this list is a bounded-height flex column, so
                // without it the collapsed rows would flex-shrink (crush) to
                // fit when a sibling expands, instead of the container scrolling.
                className="shrink-0 rounded-lg border outline-tertiary overflow-hidden"
              >
                <button
                  type="button"
                  aria-expanded={isExpanded}
                  className="flex items-center gap-2 w-full px-2.5 py-1.5 hover:surface-secondary transition-colors"
                  onClick={() =>
                    setExpandedId((prev) => (prev === row.id ? null : row.id))
                  }
                >
                  {isExpanded ? (
                    <IconChevronDown size={14} className="font-tertiary shrink-0" />
                  ) : (
                    <IconChevronRight size={14} className="font-tertiary shrink-0" />
                  )}
                  <Icon size={17} className={`${color} shrink-0`} stroke={1.6} />
                  <span className="body-3-medium flex-1 min-w-0 truncate text-left">
                    {row.name}
                  </span>
                  {/* Companion-copy rows are tagged with the companion's name so
                      it's clear this artifact goes to the plus-one, not the main
                      recipient — matches the "Name — file" prefix at real send. */}
                  {row.companionName && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded surface-blue font-blue body-4-regular shrink-0 max-w-32 truncate">
                      <IconUsers size={11} className="shrink-0" />
                      <span className="truncate">{row.companionName}</span>
                    </span>
                  )}
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded surface-secondary body-4-regular font-tertiary shrink-0">
                    {sourceLabel}
                  </span>
                  {row.type === "file" && (
                    // Desktop-only: on phones the size badge just steals width
                    // from the truncating filename.
                    <span className="body-4-regular font-tertiary shrink-0 hidden sm:inline">
                      {formatFileSize(row.size)}
                    </span>
                  )}
                </button>

                {isExpanded && (
                  <div className="border-t outline-tertiary p-2">
                    {row.type === "doc" ? (
                      <DocPreview
                        eventId={eventId}
                        documentTemplateId={row.documentTemplateId}
                        guest={row.renderGuest}
                        name={row.name}
                      />
                    ) : (
                      <FilePreview row={row} eventId={eventId} />
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {overBudget && (
            <div className="shrink-0 flex items-center gap-1.5 surface-error rounded-lg px-3 py-1.5 mt-1">
              <IconAlertTriangle size={13} className="font-error shrink-0" />
              <p className="body-4-medium font-error">
                {t("attachmentBudgetWarning")}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ── Inline preview for a single uploaded file ──
const FilePreview: React.FC<{ row: FileRow; eventId: string }> = ({
  row,
  eventId,
}) => {
  const t = useTranslations("pSendEmail");
  // Set when the <img> itself fails to load (unreachable object / CORS / 404),
  // e.g. locally where the asset host is LocalStack and the file was uploaded
  // in another env. Degrades to the same failure card + open-in-tab escape the
  // PDF branch uses, instead of the browser's native broken-image glyph.
  const [imgError, setImgError] = useState(false);

  // Category files presign on demand; direct-upload files use the asset host.
  const urlQ = useQuery({
    queryKey: ["attachmentDownloadUrl", eventId, row.attachmentId, row.attachmentFileId],
    queryFn: () =>
      getDownloadUrl(eventId, row.attachmentId!, row.attachmentFileId!),
    enabled: !!row.attachmentId && !!row.attachmentFileId,
  });

  const url = row.assetUrl
    ? row.assetUrl
    : urlQ.data && !("isServerError" in urlQ.data)
      ? urlQ.data.url
      : null;

  if (row.attachmentId && urlQ.isLoading) {
    return (
      <div className="flex items-center gap-2 py-4 justify-center font-tertiary">
        <IconLoader2 size={16} className="animate-spin" />
        <span className="body-4-regular">{t("attachmentRendering")}</span>
      </div>
    );
  }

  if (!url) {
    // Distinguish a genuine fetch failure (403/500 on the presign endpoint)
    // from a file that simply has no inline preview, mirroring DocPreview.
    const presignFailed =
      !!row.attachmentId &&
      (urlQ.isError || (!!urlQ.data && "isServerError" in urlQ.data));
    return (
      <p className="body-4-regular font-tertiary py-2 text-center">
        {presignFailed ? t("attachmentRenderFailed") : t("attachmentNoPreview")}
      </p>
    );
  }

  if (row.fileKind === "image") {
    // Broken src (unreachable object / CORS / 404) → clean failure card + an
    // open-in-tab link (top-level nav isn't subject to the img's load failure),
    // rather than the native broken-image glyph.
    if (imgError) {
      return (
        <div className="flex flex-col gap-1.5 py-2">
          <p className="body-4-regular font-tertiary text-center">
            {t("attachmentRenderFailed")}
          </p>
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 justify-center font-cta-primary hover:font-primary"
          >
            <IconExternalLink size={14} />
            <span className="body-4-medium">{t("attachmentOpenInTab")}</span>
          </a>
        </div>
      );
    }
    return (
      <img
        src={url}
        alt={row.name}
        onError={() => setImgError(true)}
        style={{ maxHeight: PREVIEW_MAX_H }}
        className="w-auto mx-auto rounded-md"
      />
    );
  }

  if (row.fileKind === "pdf") {
    // S3 presigned objects may carry an "attachment" content-disposition, which
    // makes some browsers download rather than render the iframe — so we always
    // offer an explicit open-in-tab fallback below the inline attempt.
    // Uploaded PDFs have no known page aspect, so `view=Fit` shows the WHOLE
    // page (letterboxed) in a fixed box rather than cropping a fit-to-width page.
    return (
      <div className="flex flex-col gap-1.5">
        <iframe
          src={`${url}#toolbar=0&navpanes=0&view=Fit`}
          title={row.name}
          style={{ height: PREVIEW_MAX_H }}
          className="w-full rounded-md border outline-quaternary"
        />
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1.5 justify-center font-cta-primary hover:font-primary"
        >
          <IconExternalLink size={14} />
          <span className="body-4-medium">{t("attachmentOpenInTab")}</span>
        </a>
      </div>
    );
  }

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-1.5 justify-center py-3 font-cta-primary hover:font-primary"
    >
      <IconExternalLink size={14} />
      <span className="body-4-medium">{t("attachmentOpenInTab")}</span>
    </a>
  );
};

// Shared cap for an expanded attachment preview (px). Small enough that the
// preview + its row header fit the send modal's list without cropping.
const PREVIEW_MAX_H = 256;

/**
 * Scale-to-fit wrapper: shrinks its (fixed-size) child down so the WHOLE thing
 * is visible within the container width and `maxHeight`, then reserves exactly
 * the scaled height so nothing is cropped and it centers cleanly. Used for the
 * fixed-size wallet / calendar preview cards, which are taller than the send
 * modal's attachment panel and otherwise got sliced mid-card. Never scales up
 * (cap at 1). Re-measures on container/content resize.
 */
function FitBox({
  maxHeight,
  children,
}: {
  maxHeight: number;
  children: React.ReactNode;
}) {
  const outerRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [boxH, setBoxH] = useState<number | undefined>(undefined);

  useLayoutEffect(() => {
    const outer = outerRef.current;
    const inner = innerRef.current;
    if (!outer || !inner) return;
    const measure = () => {
      const cw = outer.clientWidth;
      // scrollWidth/Height report the child's UNtransformed layout size, so the
      // ratio is stable across re-measures (no scale feedback loop).
      const iw = inner.scrollWidth;
      const ih = inner.scrollHeight;
      if (!iw || !ih) return;
      const s = Math.min(cw / iw, maxHeight / ih, 1);
      setScale(s);
      setBoxH(Math.ceil(ih * s));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(outer);
    ro.observe(inner);
    return () => ro.disconnect();
  }, [maxHeight]);

  return (
    <div
      ref={outerRef}
      className="w-full flex justify-center"
      style={{ height: boxH }}
    >
      <div
        ref={innerRef}
        style={{ transform: `scale(${scale})`, transformOrigin: "top center" }}
      >
        {children}
      </div>
    </div>
  );
}

// ── Inline preview for a per-guest document-builder PDF ──
const DocPreview: React.FC<{
  eventId: string;
  documentTemplateId: string;
  guest: Guest | null;
  name: string;
}> = ({ eventId, documentTemplateId, guest, name }) => {
  const t = useTranslations("pSendEmail");
  const tWallet = useTranslations("walletBuilder");
  const tz = useEventTimezone();
  // Type-aware formatting for `{{additionalFields.x}}` inside wallet/calendar
  // display text so the send preview matches the generated pass/event. Optional
  // provider access → degrades to raw values instead of crashing.
  const attachFieldsCtx = useFieldsOptional();
  const attachCtx = useMemo(
    () => buildPreviewFormatContext(attachFieldsCtx?.allFieldsUnfiltered ?? [], tz),
    [attachFieldsCtx, tz],
  );
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [status, setStatus] = useState<"loading" | "ready" | "error">("loading");

  const docQ = useQuery({
    queryKey: ["documentTemplateFull", eventId, documentTemplateId],
    queryFn: () => getDocumentTemplateHandler(eventId, documentTemplateId),
    enabled: !!eventId && !!documentTemplateId,
  });

  const doc: DocumentTemplate | null =
    docQ.data && !("isServerError" in docQ.data) ? docQ.data : null;

  const guestId = guest?.guestId;

  // Wallet/calendar previews render {{tokens}} as chips by design (builder
  // editors). For the send preview we substitute the selected recipient's real
  // values first (memoized on config + guest). No guest → keep raw chips.
  // One language for BOTH the drawing and the drop rule below, chosen the way
  // the SEND chooses it: against the union of keys the config actually carries.
  // Handing `guest.language` straight to a map index emptied the whole card for
  // any recipient whose tag was spelled differently from the template's
  // ("de" vs "de-DE") — a pass that ships complete, previewed as blank on the
  // last screen before Send.
  const previewLang = useMemo(
    () =>
      doc?.documentType === DocumentType.WALLET
        ? resolveWalletPreviewLang(
            doc?.walletConfig,
            guest?.language,
            doc?.primaryLanguage || "",
          )
        : guest?.language || doc?.primaryLanguage || "",
    [doc, guest?.language],
  );
  // Event-level tokens ({{eventName}}, {{eventDate}}, {{eventTime}},
  // {{eventLocation}}) are NOT guest fields. Substituting against the guest
  // alone blanked every one of them — so the last look an operator gets at a
  // pass before sending it showed empty rows the recipient will never see.
  // Active for the whole dialog, not only once a recipient is picked. The
  // wallet card is expandable before that, and without the real event it fell
  // back to the stand-in — so an operator could sit one click from Send looking
  // at "Sommerfest 2026 / Hotel de Rome". One cached fetch (staleTime 5 min)
  // for the event this dialog is already about.
  const {
    enrich: enrichWithEventVars,
    ready: eventVarsReady,
    event: previewEvent,
  } = useEventVariables(eventId, true);
  const resolvedWalletPass = useMemo(() => {
    if (!doc?.walletConfig || !guest) {
      return {
        config: doc?.walletConfig,
        dropped: [] as DroppedField[],
        uncertain: {} as Record<string, string[]>,
      };
    }
    // ...and the guest's own seat on top. `{{seatNumber}}` is enricher-gated —
    // the renderer resolves it server-side from the seating plan — so this
    // preview drew an em-dash where the delivered pass carries the seat. The
    // answer was on the guest all along (`seatInfo`, same source, same
    // arithmetic); `buildSeatVariables` refuses the one case where the two
    // sides can pick different seats. See its own docstring.
    const enrichedGuest = withSeatVariables(
      enrichWithEventVars(guest),
      buildSeatVariables(guest),
    );
    const substituted = replaceVariablesInWalletConfig(
      doc.walletConfig,
      enrichedGuest,
      attachCtx,
    );
    // And the renderer leaves out a field whose value resolved to nothing, so
    // the preview does too rather than drawing a row the pass will not carry —
    // but only once the event actually loaded. Dropping rows because a fetch
    // failed would report an outage as a short pass.
    if (!eventVarsReady)
      return {
        config: substituted,
        dropped: [] as DroppedField[],
        uncertain: {} as Record<string, string[]>,
      };
    return applyPassRenderRules(substituted, doc.walletConfig, {
      lang: previewLang,
      primaryLang: doc.primaryLanguage || previewLang,
      knows: (token) => knowsToken(enrichedGuest, token, eventVarsReady),
    });
  }, [doc, guest, attachCtx, enrichWithEventVars, eventVarsReady, previewLang]);
  const resolvedWalletConfig = resolvedWalletPass.config;
  /**
   * The rows the rule KEPT because it could not decide them — enricher-gated
   * tokens like `{{seatNumber}}`, which the pass renderer resolves from the
   * seating plan and no client can see. They are drawn with the builder's
   * em-dash, and on the screen right before Send an unexplained "PLATZ —"
   * reads as a broken preview. The builder says a sentence about them; this
   * surface computed the same set and dropped it on the floor.
   *
   * `previewLang`, the language the RULE ran in above — not the operator's tab.
   * `namedUncertainRows` requires the two to agree, or the caption names a row
   * in a language the card does not show.
   *
   * `doc.walletConfig` — the RAW config, which is what both builder call sites
   * pass and is not interchangeable with the substituted one two lines up.
   * `namedUncertainRows` picks the label's language with `pickLangKey`, whose
   * first step drops every EMPTY value from the candidate set. On a raw config
   * that filter is a no-op; on a substituted one it is not, because
   * `replaceInMultilang` writes "" back for any token this guest cannot
   * answer. A row labelled `{{additionalFields.roomLabel}}` for a guest who has
   * no such field then has no candidate language left, `pickLangKey` returns
   * null, and the caption falls back to `f.key` — the operator reads
   * "…: aux_2" beside a card that shows no internal ids anywhere. With two
   * languages it goes wrong more quietly: the rule decided on the de-DE text
   * and the caption names the row in en-US, the very divergence the paragraph
   * above promises cannot happen.
   *
   * `uncertain` is keyed by group AND field key, both of which survive
   * substitution untouched, so the lookup matches either way — only the NAME
   * differs. The sibling `dropped` caption gets this right already by going
   * through `pickPair`, which chooses the key on the raw map and reads the text
   * from the resolved one.
   */
  /**
   * What the delivered pass will DO with each undecidable row.
   *
   * `uncertain` says only "this client cannot name the value". For a seat token
   * that is settled one layer further — `guestHasSeat` decides whether a value
   * exists at all, and the renderer deletes the row when it does not. Without
   * this split the sentence under the pass told an operator, on the screen
   * right before Send, that the server would fill a row it deletes.
   */
  const certaintyRows = useMemo(
    () =>
      doc?.walletConfig
        ? classifyUncertainRows(doc.walletConfig, resolvedWalletPass.uncertain, {
            hasSeat: guestHasSeat(guest),
            rollupEmpty: (t) => activityRollupEmpty(guest, t),
            lang: previewLang,
            primaryLang: doc.primaryLanguage || previewLang,
          })
        : {},
    [doc, resolvedWalletPass, previewLang, guest],
  );
  const uncertainLabels = useMemo(
    () =>
      doc?.walletConfig
        ? namedUncertainRows(
            doc.walletConfig,
            // Only the rows the renderer will FILL — `absentNames` carries the
            // others into the warning sentence.
            pickByCertainty(certaintyRows, "awaited"),
            previewLang,
            doc.primaryLanguage || previewLang,
          )
        : [],
    [doc, certaintyRows, previewLang],
  );
  /** The undecidable rows this guest's pass will NOT carry. */
  const absentNames = useMemo(
    () =>
      doc?.walletConfig
        ? namedUncertainRows(
            doc.walletConfig,
            pickByCertainty(certaintyRows, "absent"),
            previewLang,
            doc.primaryLanguage || previewLang,
          )
        : [],
    [doc, certaintyRows, previewLang],
  );
  const resolvedCalendarConfig = useMemo(
    () =>
      doc?.calendarConfig && guest
        ? replaceVariablesInCalendarConfig(doc.calendarConfig, guest, attachCtx)
        : doc?.calendarConfig,
    [doc?.calendarConfig, guest, attachCtx],
  );

  useEffect(() => {
    let cancelled = false;
    let createdUrl: string | null = null;

    async function render() {
      if (!doc || !guestId) return;
      // WALLET/CALENDAR docs have no html/pages — they render via their own
      // config-driven previews below, never as a PDF. Skip the PDF pipeline.
      if (doc.documentType !== DocumentType.STANDARD) return;
      setStatus("loading");
      const lang = guest?.language;
      const pages =
        doc.pages && doc.pages.length > 0
          ? [...doc.pages]
              // Null-safe order (server treats missing order as 0) and drop
              // blank pages so the preview's page set matches the sent PDF.
              .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
              .map((p) => resolveLangValue(p.html, lang, doc.primaryLanguage, ""))
              .filter((h) => h.trim() !== "")
          : (() => {
              const single = resolveLangValue(
                doc.html,
                lang,
                doc.primaryLanguage,
                "",
              );
              return single ? [single] : [];
            })();

      if (pages.length === 0) {
        if (!cancelled) setStatus("error");
        return;
      }

      const result = await downloadDocumentTemplatePdfForGuest(
        eventId,
        documentTemplateId,
        guestId,
        pages,
        doc.contentAreaWidth ?? DEFAULT_DOC_WIDTH,
        doc.contentAreaHeight ?? DEFAULT_DOC_HEIGHT,
        name || "document",
      );

      if (cancelled) return;
      if (result && !("isServerError" in result)) {
        const blob = new Blob([result as BlobPart], {
          type: "application/pdf",
        });
        createdUrl = URL.createObjectURL(blob);
        setBlobUrl(createdUrl);
        setStatus("ready");
      } else {
        setStatus("error");
      }
    }

    void render();
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc, guestId, guest?.language, eventId, documentTemplateId, name]);

  // Doc-template fetch finished but returned no template (ServerError / 403) —
  // surface the failure instead of spinning forever (status stays "loading"
  // because the render effect early-returns when `doc` is null).
  if (!docQ.isLoading && !doc) {
    return (
      <p className="body-4-regular font-tertiary py-2 text-center">
        {t("attachmentRenderFailed")}
      </p>
    );
  }

  // Wallet / calendar docs aren't PDFs — render their own config-driven preview
  // (same components the builders use) instead of running them through the PDF
  // pipeline, which would always fail (no html/pages) and show "render failed".
  if (doc?.documentType === DocumentType.WALLET) {
    return doc.walletConfig ? (
      // The pass is a fixed 320×440+ card — scale it down so the WHOLE pass
      // shows in the panel instead of being cropped mid-card.
      <div className="py-1">
        <FitBox maxHeight={PREVIEW_MAX_H}>
          <WalletPreview
            config={resolvedWalletConfig!}
            currentLanguage={previewLang}
            // `!!guest`, not a bare `true`. `resolvedWalletPass` returns the
            // config UNTOUCHED when there is no recipient yet ("no guest → keep
            // raw chips", above), so claiming it was pre-substituted made
            // `WalletPreview` skip `applyEventTokenRules` over a tree that had
            // been through nothing — the one lane where that rule is the only
            // thing deciding which rows the pass will actually carry. The
            // prop's own contract says to set it on the substituted lanes and
            // only those.
            preSubstituted={!!guest}
            // What the delivered pass will do with each undecidable row. This
            // surface never turns the 1:1 view on, so nothing drawn changes
            // today — but it is the same answer the captions below are built
            // from, and one source beats two that must agree.
            rows={certaintyRows}
            event={previewEvent}
            eventId={eventId}
            primaryLanguage={doc.primaryLanguage || previewLang}
          />
        </FitBox>
        {/* The builder explains its dropped rows; this surface used to throw the
            list away and let them vanish, which on the screen right before Send
            is indistinguishable from a broken preview. */}
        {uncertainLabels.length > 0 && (
          <p
            data-testid="wallet-may-drop"
            className="flex items-start justify-center gap-1 body-4-regular font-tertiary text-center pt-1"
          >
            {/* Info, not warning: the row is kept and the renderer fills it.
                The dropped line below is the one that reports a loss, and the
                two used to arrive in the same grey. */}
            <IconInfoCircle size={12} className="mt-0.5 shrink-0" aria-hidden />
            {/* `mayDropForThisGuest`, not `mayDropForGuest`. This surface only
                ever draws for a named recipient, so the builder's generic
                sentence about "guests without a value" is the wrong one — and
                it also states the opposite of the truth. The row is not
                dropped; the renderer resolves it from data no client can see,
                so it may very well appear. The builder's own note said to write
                this sentence the moment the generic one reads wrong on a
                screen. This is that screen. */}
            <span>
              {tWallet("mayDropForThisGuest", {
                fields: uncertainLabels.join(", "),
              })}
            </span>
          </p>
        )}
        {resolvedWalletPass.dropped.length + absentNames.length > 0 && (
          <p
            data-testid="wallet-dropped-fields"
            className="flex items-start justify-center gap-1 body-4-regular font-orange text-center pt-1"
          >
            <IconAlertTriangle size={12} className="mt-0.5 shrink-0" aria-hidden />
            <span>
              {tWallet("droppedForGuest", {
                // Deduplicated — a dropped row and an absent row can carry
                // the same label, and "Stadt, Stadt" reads as two failures.
                fields: [
                  ...new Set([
                    ...resolvedWalletPass.dropped.map((d) => d.name),
                    ...absentNames,
                  ]),
                ].join(", "),
              })}
            </span>
          </p>
        )}
      </div>
    ) : (
      <p className="body-4-regular font-tertiary py-2 text-center">
        {t("attachmentNoPreview")}
      </p>
    );
  }
  if (doc?.documentType === DocumentType.CALENDAR) {
    return doc.calendarConfig ? (
      <div className="py-1">
        <FitBox maxHeight={PREVIEW_MAX_H}>
          <CalendarPreview
            config={resolvedCalendarConfig!}
            currentLanguage={previewLang}
            tz={tz}
          />
        </FitBox>
      </div>
    ) : (
      <p className="body-4-regular font-tertiary py-2 text-center">
        {t("attachmentNoPreview")}
      </p>
    );
  }

  if (docQ.isLoading || status === "loading") {
    return (
      <div className="flex items-center gap-2 py-6 justify-center font-tertiary">
        <IconLoader2 size={16} className="animate-spin" />
        <span className="body-4-regular">{t("attachmentRendering")}</span>
      </div>
    );
  }

  if (status === "error" || !blobUrl) {
    return (
      <p className="body-4-regular font-tertiary py-2 text-center">
        {t("attachmentRenderFailed")}
      </p>
    );
  }

  // Match the iframe box to the document's own aspect ratio so wide/landscape
  // templates (e.g. tickets) render fit-to-width instead of overflowing a fixed
  // portrait box with a horizontal scrollbar. `#toolbar=0&navpanes=0&view=FitH`
  // strips the native viewer's chrome (toolbar + thumbnail rail) and fits the
  // page to the iframe width. Capped height keeps tall A4 docs in-modal.
  const ratioW = doc?.contentAreaWidth ?? DEFAULT_DOC_WIDTH;
  const ratioH = doc?.contentAreaHeight ?? DEFAULT_DOC_HEIGHT;

  // Inline render + explicit open-in-tab fallback: if the browser blocks the
  // framed PDF (e.g. a strict X-Frame-Options/CSP frame policy on the host),
  // the iframe shows a blank "content blocked" box — the link is the escape
  // hatch (top-level navigation to a blob isn't subject to framing rules).
  return (
    <div className="flex flex-col gap-1.5">
      {/* Fixed HEIGHT + the doc's own aspect-ratio derive the WIDTH, so the box
          is shaped exactly like the page and `view=FitH` fills it edge-to-edge —
          the whole page shows, scaled to `PREVIEW_MAX_H`, never cropped and no
          letterbox. `max-w-full` guards a very wide (landscape) template. */}
      <div
        style={{ height: PREVIEW_MAX_H, aspectRatio: `${ratioW} / ${ratioH}` }}
        className="mx-auto max-w-full overflow-hidden rounded-md border outline-quaternary"
      >
        <iframe
          src={`${blobUrl}#toolbar=0&navpanes=0&view=FitH`}
          title={name}
          className="h-full w-full"
        />
      </div>
      <a
        href={blobUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-1.5 justify-center font-cta-primary hover:font-primary"
      >
        <IconExternalLink size={14} />
        <span className="body-4-medium">{t("attachmentOpenInTab")}</span>
      </a>
    </div>
  );
};
